import type { StoreRole } from "@hobbyshopify/backend-client";
import { can } from "../core/primitives/store-role";

/** A phone's tabs, as iOS's tab bar: Settings takes no slot, the gear beside the shop's name opens it. */
export type ShellTab = "customer" | "inventory" | "live" | "shipping" | "shows";

const TABS: readonly ShellTab[] = ["customer", "inventory", "live", "shipping", "shows"];

/**
 * The tabs a role sees, as iOS's `visibleCases(for:)`: Staff has no Live or Shipping. A role not yet known sees them
 * all, as the nav reads it, so no tab flashes away once it arrives. A capability switched off keeps its tab: the page
 * asks for it.
 */
export function tabsFor(role: StoreRole | null): readonly ShellTab[] {
  if (role === null || can(role, "hostLive")) return TABS;
  return TABS.filter((tab) => tab !== "live" && tab !== "shipping");
}

const SECTIONS: readonly [RegExp, ShellTab][] = [
  [/^\/customer-(scans|carts|qr)(\/|$)/, "customer"],
  [/^\/(inventory|cards|drafts|sealed|scans)(\/|$)/, "inventory"],
  [/^\/live(\/|$)/, "live"],
  [/^\/shipping(\/|$)/, "shipping"],
  [/^\/events(\/|$)/, "shows"],
];

/** A path as the router matches it: letter case and a trailing slash make no difference there. */
const routed = (pathname: string) => pathname.toLowerCase().replace(/(.)\/+$/, "$1");

/** The tab a page belongs to, lit in the bar; none for Settings, which the gear opens. */
export function tabOf(pathname: string): ShellTab | null {
  const path = routed(pathname);
  return SECTIONS.find(([section]) => section.test(path))?.[1] ?? null;
}

/** The pages iOS presents as sheets over their tab, and the scanner, which covers everything. */
const SHEETS: readonly RegExp[] = [
  /^\/cards\/[^/]+$/,
  /^\/(customer-)?scans\/[^/]+\/edit$/,
  /^\/events\/[^/]+(\/edit)?$/,
  /^\/live\/(sell|breaks\/[^/]+)$/,
  /^\/scan$/,
];

/** Whether a phone shows the tab bar under a page: under every page of a tab, and Settings, but no sheet. */
export function barUnder(pathname: string): boolean {
  const path = routed(pathname);
  return !SHEETS.some((sheet) => sheet.test(path));
}

/** Where a tab goes: its section's first page, and Inventory and Customer where they were last left. */
export function tabPath(tab: ShellTab, inventoryPath: string | null, customerPath: string | null = null): string {
  switch (tab) {
    case "customer":
      return customerPath ?? "/customer-scans";
    case "inventory":
      return inventoryPath ?? "/inventory";
    case "live":
      return "/live";
    case "shipping":
      return "/shipping";
    case "shows":
      return "/events";
  }
}

/**
 * Where a tab goes from the page shown. The lit tab keeps its page as it is, as iOS's tab bar re-selects a section and
 * the section keeps its pane (Customer its segment); a sheet's tab, and every other one, goes where `tabPath` says.
 */
export function tabHref(
  tab: ShellTab,
  here: { pathname: string; search: string },
  inventoryPath: string | null,
  customerPath: string | null,
): string {
  if (tab === tabOf(here.pathname) && barUnder(here.pathname)) return `${here.pathname}${here.search}`;
  return tabPath(tab, inventoryPath, customerPath);
}

const CUSTOMER_PAGES = ["/customer-scans", "/customer-carts", "/customer-qr"];

/**
 * Where the Customer tab goes back to from a page, as iOS keeps the customer served and opens on its scans: the
 * customer a Customer page carries, the list for the list, null for anything else.
 */
export function customerPathFor(pathname: string, search: string): string | null {
  const path = routed(pathname);
  if (!CUSTOMER_PAGES.includes(path)) return null;
  const customer = new URLSearchParams(search).get("customer");
  if (customer) return `/customer-scans?customer=${encodeURIComponent(customer)}`;
  return path === "/customer-scans" ? path : null;
}

/** The customer a Customer page carries, for its tabs' links: the segments stay on that customer, as iOS's do. */
export function customerQuery(customer: string | null): string {
  return customer ? `?customer=${encodeURIComponent(customer)}` : "";
}

/** A Customer page's address without the customer it carries, once the counter changes; null when there is none. */
export function withoutCustomer(pathname: string, search: string): string | null {
  const path = routed(pathname);
  const params = new URLSearchParams(search);
  if (!CUSTOMER_PAGES.includes(path) || !params.has("customer")) return null;
  params.delete("customer");
  const rest = params.toString();
  return rest ? `${path}?${rest}` : path;
}

/** The open carts' count on the Customer tab, as iOS writes it; none when there are none. */
export function cartsBadge(count: number): string | null {
  if (count <= 0) return null;
  return count > 99 ? "99+" : String(count);
}

/** Where the nav's Inventory goes back to from a page: Stock with its query, or Review; null for any other page. */
export function inventoryPathFor(pathname: string, search: string): string | null {
  if (pathname === "/cards") return `/cards${search}`;
  if (pathname === "/drafts") return "/drafts";
  if (pathname === "/sealed") return "/sealed";
  return null;
}
